import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import toast from 'react-simple-toasts'

import { Button, Select, Table } from 'slab'
import {
  getGetAdminGranularAllowanceConfigsQueryKey,
  useGetAdminGranularAllowanceConfigs,
  usePostAdminGranularAllowanceConfigsIdGenerate,
  usePostAdminGranularAllowanceConfigsIdPromote
} from 'utils/web_api'
import { type AdminGranularAllowanceConfig, AdminGranularAllowanceConfigStatus } from 'utils/web_api.schemas'

import ConfigForm from './ConfigForm'
import StateGrid from './StateGrid'
import StatusBadge from './StatusBadge'
import { toastOptions, toasttOptionsError } from '../../../utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'

interface GranularAllowanceConfigsProps {
  state_codes: string[]
}

function GranularAllowanceConfigs ({ state_codes: stateCodes }: GranularAllowanceConfigsProps) {
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<AdminGranularAllowanceConfig | null>(null)
  const [createFormOpen, setCreateFormOpen] = useState(false)
  const [editFormOpen, setEditFormOpen] = useState(false)

  const queryClient = useQueryClient()

  const { data: configs, isLoading, isError, error } = useGetAdminGranularAllowanceConfigs()

  // Get unique years and set default selection
  const availableYears = configs?.data ? [...new Set(configs.data.map(config => config.year))].sort((a, b) => b - a) : []

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0])
    }
  }, [availableYears, selectedYear])

  const filteredConfigs = configs?.data?.filter(config => config.year === selectedYear)?.sort((a, b) => b.version - a.version) || []

  const generateMutation = usePostAdminGranularAllowanceConfigsIdGenerate({
    mutation: {
      onMutate: async ({ id }) => {
        // Cancel outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })

        // Snapshot the previous value
        const previousConfigs = queryClient.getQueryData(getGetAdminGranularAllowanceConfigsQueryKey())

        // Optimistically update to the new value
        queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), (old: any) => {
          if (!old?.data) return old
          return {
            ...old,
            data: old.data.map((config: AdminGranularAllowanceConfig) =>
              config.id === id
                ? { ...config, status: AdminGranularAllowanceConfigStatus.generating }
                : config
            )
          }
        })

        // Return a context object with the snapshotted value
        return { previousConfigs }
      },
      onError: (error: any, _variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousConfigs) {
          queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), context.previousConfigs)
        }
        toast(`Failed to generate: ${error.message || 'Unknown error'}`, toasttOptionsError)
      },
      onSuccess: () => {
        toast('Generation started successfully!', toastOptions)
        setGenerateConfirmOpen(false)
        setSelectedConfig(null)
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have the latest data
        queryClient.invalidateQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })
      },
    }
  })

  const promoteMutation = usePostAdminGranularAllowanceConfigsIdPromote({
    mutation: {
      onMutate: async ({ id }) => {
        // Cancel outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })

        // Snapshot the previous value
        const previousConfigs = queryClient.getQueryData(getGetAdminGranularAllowanceConfigsQueryKey())

        // Find the config being promoted to get its year
        const configToPromote = configs?.data?.find(config => config.id === id)
        if (!configToPromote) return { previousConfigs }

        // Optimistically update to the new value
        queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), (old: any) => {
          if (!old?.data) return old
          return {
            ...old,
            data: old.data.map((config: AdminGranularAllowanceConfig) => {
              if (config.id === id) {
                // Set the promoted config as active
                return { ...config, is_active: true }
              } else if (config.year === configToPromote.year && config.is_active) {
                // Deactivate other active configs for the same year
                return { ...config, is_active: false }
              }
              return config
            })
          }
        })

        return { previousConfigs }
      },
      onError: (error: any, _variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousConfigs) {
          queryClient.setQueryData(getGetAdminGranularAllowanceConfigsQueryKey(), context.previousConfigs)
        }
        toast(`Failed to promote: ${error.message || 'Unknown error'}`, toasttOptionsError)
      },
      onSuccess: () => {
        toast('Config promoted to active successfully!', toastOptions)
        setPromoteConfirmOpen(false)
        setSelectedConfig(null)
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have the latest data
        queryClient.invalidateQueries({ queryKey: getGetAdminGranularAllowanceConfigsQueryKey() })
      },
    }
  })

  const canGenerate = (config: AdminGranularAllowanceConfig) => {
    return config.status === AdminGranularAllowanceConfigStatus.pending || config.status === AdminGranularAllowanceConfigStatus.failed
  }

  const canEdit = (config: AdminGranularAllowanceConfig) => {
    return config.status === AdminGranularAllowanceConfigStatus.pending || config.status === AdminGranularAllowanceConfigStatus.failed
  }

  const canPromote = (config: AdminGranularAllowanceConfig) => {
    return config.status === AdminGranularAllowanceConfigStatus.generated && !config.is_active
  }

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='flex justify-between items-center'>
          <div className='w-48 h-10 bg-gray-200 animate-pulse rounded' />
          <div className='w-36 h-10 bg-gray-200 animate-pulse rounded' />
        </div>
        <div className='bg-white rounded-2xl p-8 text-center'>
          <div className='animate-pulse'>
            <div className='h-4 bg-gray-200 rounded w-1/4 mx-auto mb-4' />
            <div className='h-4 bg-gray-200 rounded w-1/6 mx-auto' />
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='space-y-6'>
        <div className='flex justify-between items-center'>
          <div className='w-48'>
            <Select
              value=''
              onChange={() => {}}
              placeholder='Select plan year'
              disabled
            >
              <option value=''>No data available</option>
            </Select>
          </div>
          <Button disabled>
            Create New Version
          </Button>
        </div>
        <div className='bg-red-50 border border-red-200 rounded-2xl p-8 text-center'>
          <p className='text-red-600 font-medium mb-2'>Error loading configurations</p>
          <p className='text-red-500 text-sm'>
            {error instanceof Error ? error.message : 'Failed to load data. Please try refreshing the page.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header with Year Selector and Create Button */}
      <div className='flex justify-between items-center'>
        <div className='w-48'>
          <Select
            value={selectedYear?.toString() || ''}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            placeholder='Select plan year'
          >
            {availableYears.map(year => (
              <option key={year} value={year.toString()}>
                Plan Year {year}
              </option>
            ))}
          </Select>
        </div>

        <Button onClick={() => setCreateFormOpen(true)}>
          Create New Version
        </Button>
      </div>

      {/* Table */}
      {filteredConfigs.length > 0
        ? (
          <div className='bg-white rounded-2xl'>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.ColumnHeader>Version</Table.ColumnHeader>
                  <Table.ColumnHeader>Projected States</Table.ColumnHeader>
                  <Table.ColumnHeader>Delivered States</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {filteredConfigs.map((config) => (
                  <Table.Row key={config.id}>
                    <Table.Cell>
                      <span className='font-medium text-gray-900'>v{config.version}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <StateGrid
                        states={config.projected_data_state_codes}
                        type='projected'
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <StateGrid
                        states={config.delivered_data_state_codes}
                        type='delivered'
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={config.status} isActive={config.is_active} />
                    </Table.Cell>
                    <Table.Cell>
                      <span className='text-gray-500'>
                        {new Date(config.created_at).toLocaleDateString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className='flex gap-2'>
                        {canGenerate(config) && (
                          <Button
                            variant='tertiary'
                            size='small'
                            onClick={() => {
                              setSelectedConfig(config)
                              setGenerateConfirmOpen(true)
                            }}
                          >
                            Generate
                          </Button>
                        )}
                        {canEdit(config) && (
                          <Button
                            variant='tertiary'
                            size='small'
                            onClick={() => {
                              setSelectedConfig(config)
                              setEditFormOpen(true)
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {canPromote(config) && (
                          <Button
                            variant='tertiary'
                            size='small'
                            onClick={() => {
                              setSelectedConfig(config)
                              setPromoteConfirmOpen(true)
                            }}
                          >
                            Promote
                          </Button>
                        )}
                        {!(canGenerate(config) || canEdit(config) || canPromote(config)) && (
                          <span className='text-gray-400 text-sm'>No actions</span>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          )
        : (
          <div className='bg-white rounded-2xl p-8 text-center'>
            <p className='text-gray-500 text-lg font-medium mb-2'>No configurations found</p>
            <p className='text-gray-400 text-sm'>No configurations exist for the selected year. Create a new version to get started.</p>
          </div>
          )}

      {/* Generate Confirmation Dialog */}
      <Dialog open={generateConfirmOpen} onOpenChange={setGenerateConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Generation</DialogTitle>
            <DialogDescription>
              Are you sure you want to generate allowances for version {selectedConfig?.version} of plan year {selectedConfig?.year}?
              This process may take several minutes to complete.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='primary-destructive' onClick={() => setGenerateConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedConfig && generateMutation.mutate({ id: selectedConfig.id })}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? 'Generating...' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Confirmation Dialog */}
      <Dialog open={promoteConfirmOpen} onOpenChange={setPromoteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Promotion</DialogTitle>
            <DialogDescription>
              Are you sure you want to promote version {selectedConfig?.version} of plan year {selectedConfig?.year} to active?
              This will deactivate any currently active configuration for this year.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='primary-destructive' onClick={() => setPromoteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedConfig && promoteMutation.mutate({ id: selectedConfig.id })}
              disabled={promoteMutation.isPending}
            >
              {promoteMutation.isPending ? 'Promoting...' : 'Promote to Active'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Form */}
      <ConfigForm
        open={createFormOpen}
        onOpenChange={setCreateFormOpen}
        stateCodes={stateCodes}
        mode='create'
      />

      {/* Edit Form */}
      <ConfigForm
        open={editFormOpen}
        onOpenChange={setEditFormOpen}
        stateCodes={stateCodes}
        config={selectedConfig}
        mode='edit'
      />
    </div>
  )
}

export default GranularAllowanceConfigs
